/**
 * PreferencesContext
 * Provides global preferences state to all components
 * Ensures all components share the same preferences state
 */

import React, { createContext, useContext, useCallback, useEffect, useState, useRef } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import type { SupportedLang } from '../i18n/translations';
import { SUPPORTED_LANGS } from '../i18n/translations';
import { environment } from '../environments/environment';
import { KCI_APP_IDS } from '../eventBus/appIds';
import { kciEventBus } from '../eventBus/KCiEventBus';

/** Extract a short language code (e.g. "ja") from a locale (e.g. "ja-JP") or short code. */
function toShortCode(langOrLocale: string): SupportedLang {
  const code = langOrLocale.split('-')[0].toLowerCase();
  return (SUPPORTED_LANGS as readonly string[]).includes(code)
    ? (code as SupportedLang)
    : 'en';
}

function notifyLanguageChange(language: string): void {
  const languageTag =
     language.trim() || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
   const [lang = 'en', country = 'US'] = languageTag.split('-');
   const locale = `${lang}-${country}`;

   localStorage.setItem('SelectedLanguage', locale);

  const browserLocale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  kciEventBus.publish(
    'ClaraContextUpdated',
    {
      source: KCI_APP_IDS.SHELL,
      trigger: 'preferences',
      context: [
        { key: 'Language', value: locale },
        { key: 'ctry', value: browserLocale },
        { key: 'SCREEN.TYPE', value: 'Standalone' },
        { key: 'aiIntegrationId', value: environment.aiIntegrationId },
      ],
      clickedAt: Date.now(),
    },
    {
      source: KCI_APP_IDS.SHELL,
      target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
    }
  );

  if (typeof window !== 'undefined') {
     window.dispatchEvent(
       new CustomEvent('kci-preference-changed', { detail: { language: locale } })
     );
   }
}

export interface UserPreferences {
  theme: string;
  themeId: string;
  language: string;
  memoryEnabled: boolean;
}

interface PreferencesContextType {
  preferences: UserPreferences;
  setPreferences: (prefs: UserPreferences) => void;
  isLoading: boolean;
  error: string | null;
  updateTheme: (themeVariant: string) => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
  updateMemory: (enabled: boolean) => Promise<void>;
  themeMap: Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

// Fallback theme mapping in case API fails
const FALLBACK_THEME_MAP = new Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>([
  ['light', { id: '00000000-0000-0000-0000-000000000001', isDark: false, variables: {} }],
  ['dark', { id: '00000000-0000-0000-0000-000000000002', isDark: true, variables: {} }],
]);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const { setLang } = useI18n();
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'light',
    themeId: '',
    language: 'en',
    memoryEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [themeMap, setThemeMap] = useState<Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>>(FALLBACK_THEME_MAP);
  const hasInitializedThemes = useRef(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const cached = localStorage.getItem('kci.userPreferences.v1');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const theme = (parsed.theme === 'dark' || parsed.theme === 'light' || parsed.theme === 'midnight') ? parsed.theme : 'light';
        const language = parsed.language || 'en';
        setPreferences({
          theme,
          themeId: parsed.themeId || '',
          language,
          memoryEnabled: parsed.memoryEnabled ?? false,
        });
        // Sync I18nProvider so the shell's t() function matches the stored language.
        setLang(toShortCode(language));
        notifyLanguageChange(language);
      } catch (e) {
        console.warn('Failed to parse cached preferences', e);
      }
    }
    setIsLoading(false);
  // setLang is stable (useCallback with no deps in I18nProvider)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available themes on mount to get ID mapping
  useEffect(() => {
    if (hasInitializedThemes.current) return;
    hasInitializedThemes.current = true;

const fetchThemes = async () => {
  type AvailableTheme = {
    Id: string;
    Name: string;
    IsDefault?: boolean;
    Properties?: { Variables?: Record<string, string> };
  };

  try {
    const { getAvailableThemes: getAvailableThemesService } = await import('../services/userProfileService');
    const themes = (await getAvailableThemesService()) as AvailableTheme[];
    const mapping = new Map<string, { id: string; isDark: boolean; variables?: Record<string, string> }>();
    let defaultTheme: AvailableTheme | null = null;
        themes.forEach((theme) => {
          const lowerName = theme.Name.toLowerCase();
          const isDark = lowerName.includes('dark') || lowerName.includes('midnight');
          
          const themeInfo = {
            id: theme.Id,
            isDark,
            variables: theme.Properties?.Variables || {},
          };
          
          console.log(`[PreferencesContext] Loaded theme "${theme.Name}" (${theme.Id}) with ${Object.keys(themeInfo.variables).length} CSS variables`);
          
          mapping.set(lowerName, themeInfo);
          
          if (lowerName === 'light') {
            mapping.set('light', themeInfo);
            if (theme.IsDefault || !defaultTheme) {
              defaultTheme = theme;
            }
          } else if (isDark) {
            if (!mapping.has('dark')) {
              mapping.set('dark', themeInfo);
            }
            if (theme.IsDefault && !lowerName.includes('light')) {
              defaultTheme = theme;
            }
          }
          
          // Cache the theme variables in sessionStorage for quick access
          const varsToCache = theme.Properties?.Variables || {};
          console.log(`[PreferencesContext] Caching ${Object.keys(varsToCache).length} CSS variables for theme ${theme.Id}`);
          sessionStorage.setItem(`theme-vars-${theme.Id}`, JSON.stringify(varsToCache));
        });
        
        setThemeMap(mapping);
        
        // If current preferences don't have a themeId, resolve it by matching the stored
        // theme variant (e.g. 'dark') so we never write a mismatched variant/id pair.
        setPreferences((currentPrefs) => {
          if (!currentPrefs.themeId) {
            const matched = mapping.get(currentPrefs.theme.toLowerCase());
            const resolvedId = matched?.id ?? defaultTheme?.Id;
            if (resolvedId) {
              const updated = { ...currentPrefs, themeId: resolvedId };
              localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
              console.log(`[PreferencesContext] Resolved themeId for "${currentPrefs.theme}": ${resolvedId}`);
              return updated;
            }
          }
          return currentPrefs;
        });
      } catch (err) {
        console.warn('Failed to fetch theme mapping, using fallback:', err);
        setThemeMap(FALLBACK_THEME_MAP);
      }
    };
    fetchThemes();
  }, []);

  const updateTheme = useCallback(async (themeVariant: string) => {
    console.log(`[PreferencesContext.updateTheme] Called with variant: "${themeVariant}"`);
    setIsLoading(true);
    setError(null);
    try {
      const { updateTheme: updateThemeService } = await import('../services/userProfileService');
      
      // Look up the theme info for this variant
      let themeInfo = themeMap.get(themeVariant.toLowerCase());
      if (!themeInfo && themeMap.size === 0) {
        themeInfo = FALLBACK_THEME_MAP.get(themeVariant.toLowerCase());
      }
      
      if (!themeInfo) {
        throw new Error(`Theme variant "${themeVariant}" not found in available themes`);
      }
      
      console.log(`[PreferencesContext.updateTheme] Found theme info:`, themeInfo);
      
      // POST the UUID to backend
      await updateThemeService(themeInfo.id);
      
      // Cache theme variables if available
      if (themeInfo.variables) {
        sessionStorage.setItem(`theme-vars-${themeInfo.id}`, JSON.stringify(themeInfo.variables));
      }
      
      // Update preferences state
      const updated = { ...preferences, theme: themeVariant, themeId: themeInfo.id };
      console.log(`[PreferencesContext.updateTheme] Setting preferences to:`, updated);
      setPreferences(updated);
      localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
      console.log(`[PreferencesContext.updateTheme] ✓ Theme updated successfully`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update theme';
      setError(message);
      console.error('Theme update error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [preferences, themeMap]);

  const updateLanguage = useCallback(async (language: string) => {
    const shortCode = toShortCode(language);
    // Apply immediately so the UI reflects the new language before the backend round-trip.
    const updated = { ...preferences, language };
    setPreferences(updated);
    localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
    setLang(shortCode);
    notifyLanguageChange(language);

    setIsLoading(true);
    setError(null);
    try {
      const { updateLanguage: updateLanguageService } = await import('../services/userProfileService');
      await updateLanguageService(language);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update language';
      setError(message);
      console.error('Language update error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [preferences, setLang]);

  const updateMemory = useCallback(async (enabled: boolean) => {
    const updated = { ...preferences, memoryEnabled: enabled };
    setPreferences(updated);
    localStorage.setItem('kci.userPreferences.v1', JSON.stringify(updated));
  }, [preferences]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        setPreferences,
        isLoading,
        error,
        updateTheme,
        updateLanguage,
        updateMemory,
        themeMap,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePreferencesContext() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferencesContext must be used within PreferencesProvider');
  }
  return context;
}
